import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  IconButton,
  LANGUAGE_ENGLISH_NAMES,
  makeStyles,
  TranslationStatusChip
} from '@paintscout/ui'
import type { DialogProps, DropdownSelectOption, SupportedLang } from '@paintscout/ui'
import type { TranslationCompletionStatus } from '@paintscout/util/builder/quote/util/translation-status'
import type { QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { track } from 'shared/util/analytics'

// Mirrors WorkOrderViewDialog: pre-bakes quote.translations[lang] server-side and returns
// the per-language completion status.
const REFRESH_QUOTE_TRANSLATIONS = gql`
  mutation RefreshQuoteTranslations($quoteId: String!, $companyId: String!, $lang: String) {
    refreshQuoteTranslations(quoteId: $quoteId, companyId: $companyId, lang: $lang) {
      success
      translationStatus {
        lang
        status
      }
    }
  }
`

// Re-fetch the full quote document (JSON scalar) after generating translations so the
// freshly pre-baked quote.translations[lang] is downloaded immediately — no page refresh.
const QUOTE_QUERY = gql`
  query quote($id: String!) {
    quote(id: $id)
  }
`

const useStyles = makeStyles((theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  alert: { marginTop: theme.spacing() },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing()
  }
}))

export interface DownloadWorkOrderDialogProps extends Omit<DialogProps, 'onConfirm'> {
  availableLangs: string[]
  /** The quote being downloaded; used to generate translations and as the base for the PDF. */
  quote: QuoteDocument
  companyId: string
  /**
   * Per-language pre-bake status. If the selected language is not `complete`, generation is
   * offered inline (you'd only ever generate before downloading) before the download is allowed.
   */
  translationStatus?: Record<string, TranslationCompletionStatus>
  /**
   * Called with the language code and the freshest quote document. When a translation is
   * generated in-dialog the quote is re-fetched so the download reflects it without a refresh.
   */
  onConfirm: (lang: string | undefined, quote: QuoteDocument) => void
  onCancel: () => void
}

const ENGLISH_OPTION: DropdownSelectOption = {
  label: LANGUAGE_ENGLISH_NAMES['en'],
  value: 'en'
}

function DownloadWorkOrderDialog({
  availableLangs,
  quote,
  companyId,
  translationStatus = {},
  onConfirm,
  onCancel,
  ...dialogProps
}: DownloadWorkOrderDialogProps) {
  const classes = useStyles()
  const apolloClient = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()

  const langOptions: DropdownSelectOption[] = [
    ENGLISH_OPTION,
    ...availableLangs.map((lang) => ({
      label: LANGUAGE_ENGLISH_NAMES[lang as SupportedLang],
      value: lang
    }))
  ]

  const [selectedLang, setSelectedLang] = useState<DropdownSelectOption>(ENGLISH_OPTION)
  // Status starts from the prop, but is updated in place after an in-dialog generation so the
  // contractor can download right away rather than reopening or refreshing.
  const [status, setStatus] = useState<Record<string, TranslationCompletionStatus>>(translationStatus)
  // The quote handed to onConfirm — swapped for a freshly re-fetched copy once a translation is
  // generated so the downloaded PDF contains the new content.
  const [currentQuote, setCurrentQuote] = useState<QuoteDocument>(quote)
  const [generating, setGenerating] = useState(false)

  const lang = selectedLang.value
  const langLabel = LANGUAGE_ENGLISH_NAMES[lang as SupportedLang] ?? lang

  // English never needs generation; a non-English language must be pre-baked ('complete')
  // before its PDF is downloaded, otherwise the contractor could get untranslated content (or,
  // if generated live inside the ~15s render window, a silent English fallback — #20847).
  const needsGeneration = lang !== 'en' && status[lang] !== 'complete'

  // Title pill reflecting the selected language's state: "Generating…" while producing,
  // "<Lang> Generated" once ready, and "<Lang> Not Generated" (warning) when it still needs
  // generating. English never shows a chip.
  const chipStatus = generating
    ? 'generating'
    : lang === 'en'
      ? null
      : status[lang] === 'complete'
        ? 'translated'
        : 'untranslated'

  // Pre-bake the selected language and return the freshest quote document. Resolves to the
  // re-fetched quote on success, or null if generation didn't complete (already surfaced to the
  // user) so the caller knows not to proceed with the download.
  async function generateTranslation(): Promise<QuoteDocument | null> {
    const { data } = await apolloClient.mutate<{
      refreshQuoteTranslations?: {
        success: boolean
        translationStatus?: Array<{ lang: string; status: TranslationCompletionStatus } | null> | null
      } | null
    }>({
      mutation: REFRESH_QUOTE_TRANSLATIONS,
      variables: { quoteId: quote._id, companyId, lang }
    })

    const result = data?.refreshQuoteTranslations
    const nextStatus = result?.translationStatus ?? []
    setStatus((current) => ({
      ...current,
      ...Object.fromEntries(nextStatus.filter(Boolean).map((entry) => [entry!.lang, entry!.status]))
    }))

    // The mutation can resolve without throwing yet still not have produced the translation
    // (AI call failed/timed out and was swallowed server-side). Trust the returned status.
    const langStatus = nextStatus.find((entry) => entry?.lang === lang)?.status
    if (!result?.success || langStatus !== 'complete') {
      enqueueSnackbar(`Unable to generate ${langLabel} translation. Please try again.`, { variant: 'error' })
      return null
    }

    // Pull the freshly pre-baked translations into the quote that will be downloaded, so the
    // PDF reflects them immediately (Alex's QA: no page refresh required before download).
    const refetched = await apolloClient.query<{ quote?: QuoteDocument }>({
      query: QUOTE_QUERY,
      variables: { id: quote._id },
      fetchPolicy: 'network-only'
    })
    const fresh = refetched?.data?.quote ?? currentQuote
    setCurrentQuote(fresh)

    track('work_order_translation_generated', { quoteId: quote._id, lang })
    return fresh
  }

  // Single primary action: generation is tied into the download (per product) — if the selected
  // language isn't ready we generate it first, then download the freshly translated quote in the
  // same click. No separate "Generate" step or blocking alert.
  async function handleDownload() {
    if (!needsGeneration) {
      onConfirm(lang === 'en' ? undefined : lang, currentQuote)
      return
    }

    setGenerating(true)
    try {
      const fresh = await generateTranslation()
      if (fresh) {
        onConfirm(lang, fresh)
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { source: 'DownloadWorkOrderDialog.handleDownload', lang } })
      const code = (err as any)?.graphQLErrors?.[0]?.extensions?.code
      enqueueSnackbar(
        code === 'TRANSLATION_UNAVAILABLE'
          ? 'Translation is not available right now. Please contact support.'
          : 'Unable to generate translation. Please try again later.',
        { variant: 'error' }
      )
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Dialog {...dialogProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={generating}>
            <CloseIcon />
          </IconButton>
        }
      >
        <span className={classes.titleRow}>
          Download Work Order
          {chipStatus && <TranslationStatusChip status={chipStatus} language={langLabel} />}
        </span>
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        <DropdownSelect
          variant="single"
          label="Language"
          options={langOptions}
          value={selectedLang}
          disabled={generating}
          onChange={(option) => setSelectedLang(option as DropdownSelectOption)}
        />
        {generating && (
          <Alert severity="info" className={classes.alert}>
            Generating {langLabel} translation — this will download automatically once it's ready.
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="text" onClick={onCancel} disabled={generating}>
            Cancel
          </Button>
        }
      >
        <Button variant="contained" color="primary" loading={generating} disabled={generating} onClick={handleDownload}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadWorkOrderDialog
